<script setup lang="ts">
import { computed } from 'vue';
import { CheckboxBase as Checkbox } from '@/features/ui-kit/checkbox';
import { DropdownButton } from '@/features/ui-kit/form';

import { FeedbackFilterType, FeedbackSortType } from '@/shared/apollo-api/__generated__/types';

interface DropdownItem {
  value: FeedbackSortType;
  title: string;
}

interface Props {
  filters?: FeedbackFilterType[];
  sort?: FeedbackSortType;
  showCheckbox?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  filters: () => [],
  sort: () => FeedbackSortType.Relevance,
  showCheckbox: true,
});

const emit = defineEmits<{
  filtersChanged: [filters: FeedbackFilterType[]];
  sortChanged: [sort: FeedbackSortType];
}>();

const hasPhotos = computed(() => {
  return props.filters.some((f) => f === FeedbackFilterType.Photos);
});

const onPhotoCheckboxClick = () => {
  if (hasPhotos.value) {
    emit('filtersChanged', []);
  } else {
    emit('filtersChanged', [FeedbackFilterType.Photos]);
  }
};

const onSortSelected = (s: FeedbackSortType) => {
  emit('sortChanged', s);
};

const sorts = computed((): DropdownItem[] => {
  return [
    { value: FeedbackSortType.Relevance, title: 'Популярные' },
    { value: FeedbackSortType.DateDesc, title: 'Новые' },
    { value: FeedbackSortType.RatingDesc, title: 'С высокой оценкой' },
    { value: FeedbackSortType.RatingAsc, title: 'С низкой оценкой' },
  ];
});

const currentSort = computed(() => {
  return sorts.value.find((sort) => sort.value === props.sort) || sorts.value[0];
});
</script>

<template>
  <div class="filters">
    <DropdownButton
      class="sort-dropdown"
      :options="sorts"
      :model-value="currentSort"
      placeholder="Выберите сортировку"
      @update:model-value="(option) => onSortSelected(option.value as FeedbackSortType)"
    >
      <template #button="slotProps: any">
        {{ (slotProps.option as DropdownItem)?.title || 'Выберите сортировку' }}
      </template>
      <template #option="slotProps: any">
        {{ (slotProps.option as DropdownItem).title }}
      </template>
    </DropdownButton>
    <template v-if="showCheckbox">
      <label class="filter-item">
        <Checkbox :model-value="hasPhotos" @update:model-value="onPhotoCheckboxClick" />
        Отзывы только с фото
      </label>
    </template>
  </div>
</template>

<style scoped lang="stylus">
.filters
  display flex
  gap 20px
  padding 14px 0
  border-top 1px solid rgba(54, 54, 64, 0.08)

.filter-item
  display flex
  gap 12px
  align-items center

.sort-dropdown
  max-width 280px
</style>
